import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  MouseEvent,
} from 'react';

export interface RouteLocation {
  pathname: string;
  search: string;
  hash: string;
}

interface RouterContextValue {
  location: RouteLocation;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterContextValue>({
  location: { pathname: '/', search: '', hash: '' },
  navigate: () => {},
});

const RouteParamsContext = createContext<Record<string, string>>({});

function readWindowLocation(): RouteLocation {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', hash: '' };
  }
  return {
    pathname: window.location.pathname || '/',
    search: window.location.search || '',
    hash: window.location.hash || '',
  };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<RouteLocation>(() => readWindowLocation());

  useEffect(() => {
    const handlePopState = () => {
      setLocation(readWindowLocation());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (typeof window === 'undefined') return;
    if (options?.replace) {
      window.history.replaceState({}, '', to);
    } else {
      window.history.pushState({}, '', to);
    }
    setLocation(readWindowLocation());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useLocation(): RouteLocation {
  return useContext(RouterContext).location;
}

export function useNavigate(): (to: string, options?: { replace?: boolean }) => void {
  return useContext(RouterContext).navigate;
}

export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  return useContext(RouteParamsContext) as T;
}

export function useSearchParams(): [URLSearchParams, (next: Record<string, string | null>) => void] {
  const { location, navigate } = useContext(RouterContext);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const setParams = useCallback(
    (next: Record<string, string | null>) => {
      const updated = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === '') {
          updated.delete(k);
        } else {
          updated.set(k, v);
        }
      }
      const qs = updated.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  return [params, setParams];
}

export function matchPathPattern(
  pattern: string,
  pathname: string,
): Record<string, string> | null {
  const cleanPattern = pattern.replace(/\/+$/, '') || '/';
  const cleanPath = pathname.replace(/\/+$/, '') || '/';

  if (cleanPattern === '*') {
    return {};
  }

  const patternSegments = cleanPattern.split('/').filter(Boolean);
  const pathSegments = cleanPath.split('/').filter(Boolean);

  if (patternSegments.length !== pathSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let i = 0; i < patternSegments.length; i++) {
    const pat = patternSegments[i];
    const seg = pathSegments[i];
    if (pat.startsWith(':')) {
      params[pat.slice(1)] = decodeURIComponent(seg);
    } else if (pat !== seg) {
      return null;
    }
  }

  return params;
}

export interface RouteDefinition {
  path: string;
  element: ReactNode;
}

export function RoutesSwitch({
  routes,
  fallback,
}: {
  routes: RouteDefinition[];
  fallback?: ReactNode;
}) {
  const { location } = useContext(RouterContext);

  for (const route of routes) {
    const matchedParams = matchPathPattern(route.path, location.pathname);
    if (matchedParams !== null) {
      return (
        <RouteParamsContext.Provider value={matchedParams}>
          {route.element}
        </RouteParamsContext.Provider>
      );
    }
  }

  return <>{fallback ?? null}</>;
}

export interface LinkProps {
  to: string;
  className?: string;
  title?: string;
  children?: ReactNode;
  onClick?: () => void;
}

export function Link({ to, className, title, children, onClick }: LinkProps) {
  const navigate = useNavigate();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    onClick?.();
    navigate(to);
  };

  return (
    <a href={to} onClick={handleClick} className={className} title={title}>
      {children}
    </a>
  );
}
